import { useEffect, useState, useCallback } from 'react';
import { Room, ThrowEvent, ReactionEvent, DeckType } from '../../domain/entities/Room';
import { FirebaseRoomRepository } from '../../infrastructure/firebase/FirebaseRoomRepository';
import { FirebaseGameLimitService } from '../../infrastructure/firebase/FirebaseGameLimitService';
import { CreateRoomUseCase } from '../../app/usecases/CreateRoomUseCase';
import { VoteUseCase } from '../../app/usecases/VoteUseCase';
import { RevealVotesUseCase } from '../../app/usecases/RevealVotesUseCase';
import { incrementStat } from '../../infrastructure/firebase/StatsService';

const roomRepo = new FirebaseRoomRepository();
const gameLimitService = new FirebaseGameLimitService();
const createRoomUseCase = new CreateRoomUseCase(roomRepo, gameLimitService);
const voteUseCase = new VoteUseCase(roomRepo);
const revealVotesUseCase = new RevealVotesUseCase(roomRepo);

export function useRoom(roomId: string | null) {
  const [room, setRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!roomId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = roomRepo.subscribeToRoom(roomId, (r) => {
      setRoom(r);
      setLoading(false);
    });
    return unsubscribe;
  }, [roomId]);

  const createRoom = useCallback(async (
    name: string,
    adminId: string,
    adminName: string,
    adminAnimal: string
  ) => {
    setError(null);
    try {
      const id = await createRoomUseCase.execute(name, adminId, adminName, adminAnimal);
      incrementStat('totalRooms');
      return id;
    } catch (e: any) {
      setError(e.message);
      throw e;
    }
  }, []);

  const joinRoom = useCallback(async (rId: string, userId: string, name: string, animal: string, isAdmin: boolean) => {
    await roomRepo.joinRoom(rId, {
      userId,
      name,
      animal,
      vote: null,
      isAdmin,
      isConnected: true,
      joinedAt: Date.now(),
    });
  }, []);

  const vote = useCallback(async (rId: string, userId: string, card: string, deckType: DeckType = 'fibonacci') => {
    await voteUseCase.execute(rId, userId, card, deckType);
  }, []);

  const reveal = useCallback(async (rId: string, story: string, votes: Record<string, string>, deckType: DeckType = 'fibonacci') => {
    return revealVotesUseCase.execute(rId, story, votes, deckType);
  }, []);

  const reset = useCallback(async (rId: string, newStory?: string) => {
    await roomRepo.resetVoting(rId, newStory);
  }, []);

  const setStory = useCallback(async (rId: string, story: string) => {
    await roomRepo.setCurrentStory(rId, story);
  }, []);

  const setDeckType = useCallback(async (rId: string, deckType: DeckType) => {
    await roomRepo.setDeckType(rId, deckType);
  }, []);

  const leaveRoom = useCallback(async (rId: string, userId: string) => {
    await roomRepo.leaveRoom(rId, userId);
  }, []);

  const deleteRoom = useCallback(async (rId: string) => {
    await roomRepo.deleteRoom(rId);
  }, []);

  const sendThrow = useCallback(async (rId: string, event: ThrowEvent) => {
    await roomRepo.sendThrow(rId, event);
  }, []);

  const sendReaction = useCallback(async (rId: string, event: ReactionEvent) => {
    await roomRepo.sendReaction(rId, event);
  }, []);

  return {
    room,
    loading,
    error,
    createRoom,
    joinRoom,
    vote,
    reveal,
    reset,
    setStory,
    setDeckType,
    leaveRoom,
    deleteRoom,
    sendThrow,
    sendReaction,
  };
}
